import React, { useEffect, useState, useRef } from "react";
import L from "leaflet";
import { CircleMarker, Popup, Tooltip } from "react-leaflet";
import axios from "axios";
import { API } from "../App";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Sunrise, Sunset, MapPin, Upload, Trash2, User, Clock, Settings2 } from "lucide-react";

/**
 * HighSeatsLayer – prikaz visokih prež na karti + modal za rezervacije.
 * Props:
 *  - token: JWT
 *  - user: current user (za role check + prikaz "moja rezervacija")
 *  - enabled: prikazi/skriji sloj
 *  - onCountChange(n): opcijski callback (za layer picker prikaz števila)
 *  - refreshKey: bump za force-refresh
 */
export default function HighSeatsLayer({ token, user, enabled, onCountChange, refreshKey = 0 }) {
  const [prezas, setPrezas] = useState([]);
  const [selectedPreza, setSelectedPreza] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [settings, setSettings] = useState({ enabled: true, hours_before: 2, release_hours_after: 2, max_advance_days: 0 });
  const [dogodek, setDogodek] = useState("mrak");
  const [saving, setSaving] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const [members, setMembers] = useState([]);
  const [targetClanId, setTargetClanId] = useState(""); // "" = zase, sicer id drugega člana

  // Odštevalnik: osveži nowTs vsako sekundo, ko je modal odprt
  useEffect(() => {
    if (!modalOpen) return;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [modalOpen]);

  const canManage = ["predsednik", "gospodar", "administrator", "super_admin"].includes(user?.vloga);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [seatsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/high-seats`, { headers }),
        axios.get(`${API}/high-seats/settings`, { headers }).catch(() => ({ data: null })),
      ]);
      setPrezas(seatsRes.data || []);
      if (settingsRes.data) setSettings(settingsRes.data);
      if (onCountChange) onCountChange((seatsRes.data || []).length);
    } catch (err) {
      console.error("Napaka pri nalaganju prež:", err);
    }
  };

  // Naloži seznam članov (samo za vodstvo, za rezervacijo v tujem imenu)
  const fetchMembers = async () => {
    if (!token) return;
    if (!["predsednik", "gospodar", "administrator", "super_admin"].includes(user?.vloga)) return;
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      const list = (res.data || []).filter((u) => u.id !== user?.id && !u.is_demo).sort((a, b) =>
        `${a.priimek || ""} ${a.ime || ""}`.localeCompare(`${b.priimek || ""} ${b.ime || ""}`, "sl-SI")
      );
      setMembers(list);
    } catch (err) {
      console.warn("Napaka pri branju članov:", err?.message);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchData();
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshKey]);

  const openReserveModal = (preza) => {
    setSelectedPreza(preza);
    setDogodek("mrak");
    setTargetClanId("");
    setModalOpen(true);
  };

  const handleReserve = async () => {
    if (!selectedPreza) return;
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const today = new Date().toISOString().slice(0, 10);
      const payload = { preza_id: selectedPreza.id, datum: today, dogodek };
      if (targetClanId) payload.clan_id = targetClanId;
      await axios.post(`${API}/high-seats/${selectedPreza.id}/reserve`, payload, { headers });
      const targetName = targetClanId
        ? (members.find((m) => m.id === targetClanId) ? `${members.find((m) => m.id === targetClanId).ime} ${members.find((m) => m.id === targetClanId).priimek}` : "")
        : "";
      toast.success(targetName
        ? `Preža "${selectedPreza.ime}" rezervirana za ${targetName}`
        : `Preža "${selectedPreza.ime}" rezervirana`);
      setModalOpen(false);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend zavrnitev "okno še ni odprto" (425): detail je dict {message, opens_at, event_at}
      if (err?.response?.status === 425 && detail && typeof detail === "object") {
        const opensLocal = new Date(detail.opens_at).toLocaleString("sl-SI", {
          hour: "2-digit", minute: "2-digit", day: "numeric", month: "short"
        });
        toast.error(`Rezervacija še ni možna. Okno se odpre ${opensLocal}.`);
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || err.message || "Napaka pri rezervaciji");
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // Vrne countdown info za trenutno izbrani dogodek
  const getWindowInfo = () => {
    if (!selectedPreza?.today_windows?.[dogodek]) return null;
    const w = selectedPreza.today_windows[dogodek];
    const opensAt = new Date(w.opens_at).getTime();
    const closesAt = new Date(w.closes_at).getTime();
    const eventAt = new Date(w.event_at).getTime();
    const secToOpen = Math.max(0, Math.floor((opensAt - nowTs) / 1000));
    const isOpen = nowTs >= opensAt && nowTs <= closesAt;
    const isPast = nowTs > closesAt;
    return { opensAt, closesAt, eventAt, secToOpen, isOpen, isPast };
  };

  const formatCountdown = (sec) => {
    if (sec <= 0) return "0:00";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleCancel = async (resId, prezaIme) => {
    if (!window.confirm(`Prekličem rezervacijo preže "${prezaIme}"?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/high-seats/reservations/${resId}`, { headers });
      toast.success("Rezervacija preklicana");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Napaka");
    }
  };

  const handleDeleteSeat = async () => {
    if (!selectedPreza || !canManage) return;
    if (!window.confirm(`Izbrišem prežo "${selectedPreza.ime}"? To bo zbrisalo tudi vse rezervacije.`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/high-seats/${selectedPreza.id}`, { headers });
      toast.success("Preža izbrisana");
      setModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Napaka");
    }
  };

  if (!enabled) return null;

  return (
    <>
      {prezas.map((p) => {
        const zasedena = !!p.zasedeno;
        const isMine = zasedena && p.rezerviral_id === user?.id;
        const color = isMine ? "#f59e0b" : zasedena ? "#dc2626" : "#16a34a";
        return (
          <CircleMarker
            key={p.id}
            center={[p.lat, p.lng]}
            radius={9}
            bubblingMouseEvents={false}
            pathOptions={{ color: "#fff", weight: 2, fillColor: color, fillOpacity: 0.95 }}
            eventHandlers={{
              click: (e) => {
                if (e?.originalEvent) {
                  e.originalEvent.stopPropagation();
                  if (typeof L !== "undefined" && L.DomEvent) {
                    L.DomEvent.stopPropagation(e.originalEvent);
                  }
                }
                openReserveModal(p);
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -8]}>
              <div className="text-xs font-semibold">
                {p.ime}
                {zasedena && (
                  <div className="text-[10px] font-normal text-red-600">
                    {isMine ? "moja rezervacija" : `zasedena · ${p.rezerviral_ime || ""}`}
                  </div>
                )}
                {!zasedena && <div className="text-[10px] font-normal text-emerald-600">prosta</div>}
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}

      {selectedPreza && (
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-md" data-testid="high-seat-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-emerald-600" />
                {selectedPreza.ime}
              </DialogTitle>
              <DialogDescription>
                Visoka preža · {selectedPreza.lat.toFixed(5)}, {selectedPreza.lng.toFixed(5)}
              </DialogDescription>
            </DialogHeader>

            {/* Današnje rezervacije */}
            {(selectedPreza.today_reservations?.length || 0) > 0 && (
              <div className="rounded border border-red-200 bg-red-50 p-3 space-y-2">
                <div className="text-xs font-semibold text-red-700 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Današnje rezervacije
                </div>
                {selectedPreza.today_reservations.map((r) => {
                  const mine = r.clan_id === user?.id;
                  return (
                    <div key={r.id} className="flex items-center justify-between text-xs">
                      <div>
                        <span className="font-medium">
                          {r.dogodek === "svit" ? <Sunrise className="h-3 w-3 inline mr-1" /> : <Sunset className="h-3 w-3 inline mr-1" />}
                          {r.dogodek === "svit" ? "Jutranji lov" : "Večerni lov"}
                        </span>
                        <span className="text-slate-600 ml-2">{r.start_at.slice(11,16)}–{r.end_at.slice(11,16)}</span>
                        <div className="text-slate-500">
                          <User className="h-3 w-3 inline mr-0.5" />
                          {r.clan_ime || "—"}
                        </div>
                      </div>
                      {(mine || canManage) && (
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600"
                          data-testid={`cancel-res-${r.id}`}
                          onClick={() => handleCancel(r.id, selectedPreza.ime)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rezervacijski obrazec */}
            <div className="space-y-3">
              <div>
                <Label className="text-sm">Izberi dogodek</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    data-testid="dogodek-svit"
                    onClick={() => setDogodek("svit")}
                    className={`p-2 rounded border text-sm flex items-center justify-center gap-1.5 ${dogodek === "svit" ? "border-amber-500 bg-amber-50 text-amber-900 font-semibold" : "border-slate-200"}`}
                  >
                    <Sunrise className="h-4 w-4" /> Jutranji lov
                  </button>
                  <button
                    type="button"
                    data-testid="dogodek-mrak"
                    onClick={() => setDogodek("mrak")}
                    className={`p-2 rounded border text-sm flex items-center justify-center gap-1.5 ${dogodek === "mrak" ? "border-indigo-500 bg-indigo-50 text-indigo-900 font-semibold" : "border-slate-200"}`}
                  >
                    <Sunset className="h-4 w-4" /> Večerni lov
                  </button>
                </div>
              </div>

              {/* Vodstvo lahko rezervira za drugega člana */}
              {canManage && members.length > 0 && (
                <div>
                  <Label className="text-sm">Rezerviraj za</Label>
                  <select
                    value={targetClanId}
                    onChange={(e) => setTargetClanId(e.target.value)}
                    data-testid="reserve-on-behalf-select"
                    className="mt-1 w-full h-9 rounded border border-slate-200 bg-white text-sm px-2"
                  >
                    <option value="">Zase ({user?.ime} {user?.priimek})</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.priimek} {m.ime}
                      </option>
                    ))}
                  </select>
                  {targetClanId && (
                    <p className="text-[11px] text-indigo-700 mt-1">
                      Rezervacija bo evidentirana v imenu izbranega člana. Ti si vpisan kot rezerviral.
                    </p>
                  )}
                </div>
              )}

              <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">
                Rezervacija velja {settings.hours_before || 2}h pred {dogodek === "svit" ? "svitom" : "mrakom"} in {settings.release_hours_after || 2}h po {dogodek === "svit" ? "svitu" : "mraku"}.
                {canManage && <span className="block mt-1 text-[10px] text-slate-400">Časovni okvir določi vodstvo v nastavitvah.</span>}
              </div>

              {(() => {
                const w = getWindowInfo();
                if (!w) return null;
                if (w.isOpen) {
                  const closesLocal = new Date(w.closesAt).toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" });
                  return (
                    <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Okno je <b>odprto</b> · zapre se ob {closesLocal}.</span>
                    </div>
                  );
                }
                if (w.isPast) {
                  return (
                    <div className="text-xs text-slate-700 bg-slate-100 border border-slate-200 rounded p-2 flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Okno je bilo zaprto danes. Rezervacije bodo možne jutri.</span>
                    </div>
                  );
                }
                // Not yet open → countdown
                const opensLocal = new Date(w.opensAt).toLocaleTimeString("sl-SI", { hour: "2-digit", minute: "2-digit" });
                return (
                  <div className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded p-2" data-testid="reservation-countdown">
                    <div className="flex items-center gap-2 font-semibold">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Rezervacija še ni možna</span>
                    </div>
                    <div className="mt-1 text-[11px]">
                      Na voljo bo čez <b className="text-amber-950">{formatCountdown(w.secToOpen)}</b> (ob {opensLocal}).
                    </div>
                  </div>
                );
              })()}

              {settings.enabled === false && (
                <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 font-medium">
                  Rezervacije prež so trenutno izklopljene s strani vodstva.
                </div>
              )}

              {selectedPreza.opis && (
                <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">{selectedPreza.opis}</div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              {canManage && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="mr-auto"
                  data-testid="delete-seat-btn"
                  onClick={handleDeleteSeat}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Izbriši prežo
                </Button>
              )}
              <Button variant="outline" onClick={() => setModalOpen(false)}>Zapri</Button>
              {(() => {
                const w = getWindowInfo();
                const notYetOpen = w && !w.isOpen && !w.isPast;
                const disabled = saving || settings.enabled === false || notYetOpen;
                return (
                  <Button onClick={handleReserve} disabled={disabled} data-testid="reserve-btn">
                    {saving ? "Rezerviram…" : "Rezerviraj"}
                  </Button>
                );
              })()}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}


/**
 * HighSeatsAdminPanel – majhen panel v mape UI za predsednika/gospodarja:
 *  - dodaj eno prežo (ročno)
 *  - uvoz iz CSV/KML/GPX
 *  - upravljaj nastavitve (allowed_hours_before, release_hours_after)
 */
export function HighSeatsAdminPanel({ token, user, onImported }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("add"); // add | import | settings
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [manual, setManual] = useState({ ime: "", lat: "", lng: "", gk_x: "", gk_y: "", opis: "" });
  const [settings, setSettings] = useState({ enabled: true, hours_before: 2, release_hours_after: 2, max_advance_days: 0 });
  const [savingSettings, setSavingSettings] = useState(false);

  const canManage = ["predsednik", "gospodar", "administrator", "super_admin"].includes(user?.vloga);

  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API}/high-seats/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSettings(r.data))
      .catch(() => {});
  }, [open, token]);

  if (!canManage) return null;

  const handleFileImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Izberi datoteko"); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/high-seats/import-file`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setImportResult(res.data);
      toast.success(`Uvoženih: ${res.data.added} prež (napak: ${res.data.errors?.length || 0})`);
      if (onImported) onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Napaka pri uvozu");
    } finally {
      setImporting(false);
    }
  };

  const handleManualAdd = async () => {
    if (!manual.ime.trim()) { toast.error("Vnesi ime preže"); return; }
    let payload;
    if (manual.lat && manual.lng) {
      payload = { ime: manual.ime.trim(), lat: parseFloat(manual.lat), lng: parseFloat(manual.lng), opis: manual.opis || null };
    } else if (manual.gk_x && manual.gk_y) {
      // Backend bulk-import zna GK; uporabi bulk-import (1 item)
      try {
        await axios.post(`${API}/high-seats/import`, {
          items: [{
            ime: manual.ime.trim(),
            gk_x: parseFloat(manual.gk_x),
            gk_y: parseFloat(manual.gk_y),
            opis: manual.opis || null,
          }],
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Preža dodana");
        setManual({ ime: "", lat: "", lng: "", gk_x: "", gk_y: "", opis: "" });
        if (onImported) onImported();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Napaka");
      }
      return;
    } else {
      toast.error("Vnesi lat/lng ali GK koordinate");
      return;
    }
    try {
      await axios.post(`${API}/high-seats`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preža dodana");
      setManual({ ime: "", lat: "", lng: "", gk_x: "", gk_y: "", opis: "" });
      if (onImported) onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Napaka");
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/high-seats/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Nastavitve shranjene");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Napaka");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="high-seats-admin-btn"
        title="Upravljaj visoke preže"
      >
        <Settings2 className="h-3 w-3 mr-1" /> Preže
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="high-seats-admin-dialog">
          <DialogHeader>
            <DialogTitle>Upravljanje visokih prež</DialogTitle>
          </DialogHeader>

          <div className="flex gap-1 border-b">
            {[
              { k: "add", l: "Dodaj" },
              { k: "import", l: "Uvoz iz datoteke" },
              { k: "settings", l: "Nastavitve" },
            ].map(t => (
              <button
                key={t.k}
                data-testid={`hs-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`px-3 py-1.5 text-sm ${tab === t.k ? "border-b-2 border-emerald-600 font-semibold" : "text-slate-600"}`}
              >{t.l}</button>
            ))}
          </div>

          {tab === "add" && (
            <div className="space-y-3">
              <div>
                <Label>Ime preže</Label>
                <Input value={manual.ime} onChange={(e) => setManual({ ...manual, ime: e.target.value })} data-testid="hs-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>WGS84 lat</Label>
                  <Input value={manual.lat} onChange={(e) => setManual({ ...manual, lat: e.target.value })} placeholder="45.78" />
                </div>
                <div>
                  <Label>WGS84 lng</Label>
                  <Input value={manual.lng} onChange={(e) => setManual({ ...manual, lng: e.target.value })} placeholder="14.30" />
                </div>
              </div>
              <div className="text-center text-xs text-slate-400">— ali GK D48 —</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>GK x (easting)</Label>
                  <Input value={manual.gk_x} onChange={(e) => setManual({ ...manual, gk_x: e.target.value })} placeholder="445448" />
                </div>
                <div>
                  <Label>GK y (northing)</Label>
                  <Input value={manual.gk_y} onChange={(e) => setManual({ ...manual, gk_y: e.target.value })} placeholder="83693" />
                </div>
              </div>
              <div>
                <Label>Opomba</Label>
                <Textarea rows={2} value={manual.opis} onChange={(e) => setManual({ ...manual, opis: e.target.value })} />
              </div>
              <Button onClick={handleManualAdd} className="w-full" data-testid="hs-add-submit">Dodaj prežo</Button>
            </div>
          )}

          {tab === "import" && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">
                Podprti formati: <b>.csv</b>, <b>.kml</b>, <b>.gpx</b>. Za CSV podpiramo tudi kataster
                LZS format s stolpci <code>koordinata y</code>, <code>koordinata x</code>, <code>krajevno ime objekta</code>.
                Koordinate se avtomatsko pretvorijo iz GK D48 v WGS84.
              </div>
              <input ref={fileRef} type="file" accept=".csv,.kml,.gpx" data-testid="hs-file-input" className="block w-full text-sm" />
              <Button onClick={handleFileImport} disabled={importing} className="w-full" data-testid="hs-import-submit">
                <Upload className="h-4 w-4 mr-2" />{importing ? "Uvažam…" : "Naloži in uvozi"}
              </Button>
              {importResult && (
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
                  <div>Uvoženih: <b>{importResult.added}</b> prež</div>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-1 text-red-700">
                      Napake ({importResult.errors.length}):
                      <ul className="list-disc list-inside">
                        {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>vrstica {e.i}: {e.err}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "settings" && (
            <div className="space-y-3">
              {/* Master enable/disable toggle */}
              <div className={`flex items-center justify-between rounded border p-3 ${settings.enabled ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                <div>
                  <div className={`text-sm font-semibold ${settings.enabled ? "text-emerald-900" : "text-red-900"}`}>
                    Rezervacije prež so {settings.enabled ? "vklopljene" : "izklopljene"}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Ko je izklopljeno, člani ne morejo rezervirati prež. Preže so še vedno vidne na karti.
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={settings.enabled}
                  data-testid="hs-settings-enabled-toggle"
                  onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${settings.enabled ? "bg-emerald-600" : "bg-slate-300"}`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${settings.enabled ? "translate-x-5" : "translate-x-0.5"}`}
                  />
                </button>
              </div>

              <div className={settings.enabled ? "" : "opacity-50 pointer-events-none"}>
                <Label>Ur pred svitom/mrakom</Label>
                <div className="flex gap-2 mt-1">
                  {[1,2,3,4,5,6].map(h => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setSettings({ ...settings, hours_before: h })}
                      data-testid={`hs-settings-hours-before-${h}`}
                      className={`px-3 py-1.5 rounded border text-sm ${settings.hours_before === h ? "border-emerald-600 bg-emerald-50 font-semibold" : "border-slate-200"}`}
                    >{h}h</button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Sistemska nastavitev za vse preže. Član sam ne izbira, samo rezervira jutranji ali večerni lov.</p>
              </div>
              <div className={settings.enabled ? "" : "opacity-50 pointer-events-none"}>
                <Label>Sprostitev {settings.release_hours_after}h po svitu/mraku</Label>
                <input
                  type="range"
                  min={0}
                  max={4}
                  value={settings.release_hours_after}
                  onChange={(e) => setSettings({ ...settings, release_hours_after: parseInt(e.target.value) })}
                  className="w-full accent-emerald-600"
                  data-testid="hs-settings-release"
                />
                <div className="flex justify-between text-[11px] text-slate-500">
                  <span>0h</span><span>1h</span><span>2h</span><span>3h</span><span>4h</span>
                </div>
              </div>
              <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">
                Rezervacije so mogoče samo za <b>današnji dan</b>.
              </div>
              <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full" data-testid="hs-settings-save">
                {savingSettings ? "Shranjujem…" : "Shrani nastavitve"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
